import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  beginTextStyleEditOperation,
  cancelTextStyleEditOperation,
  commitTextStyleEditOperation,
  TextStyleEditSession,
} from 'editor/src/store/design/operation/textStyleEditSession';
import updateTextStyleOperation from 'editor/src/store/design/operation/updateTextStyleOperation';
import { TextStyle } from 'editor/src/store/design/types';
import { TextAlign } from 'editor/src/store/fonts/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import { getBrowserFriendlyColor } from 'editor/src/util/color/colorUtils';
import toastController from 'editor/src/util/toastController';
import useOnClickOutside from 'editor/src/util/useOnClickOutside';
import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';
import ColorPicker from 'editor/src/component/ColorPicker';
import H1 from 'editor/src/component/Header/H1';
import IconArrowLeft from 'editor/src/component/Icon/IconArrowLeft';
import IconStrikethrough from 'editor/src/component/Icon/IconStrikethrough';
import IconTextAlignCenter from 'editor/src/component/Icon/IconTextAlignCenter';
import IconTextAlignLeft from 'editor/src/component/Icon/IconTextAlignLeft';
import IconTextAlignRight from 'editor/src/component/Icon/IconTextAlignRight';
import IconUnderline from 'editor/src/component/Icon/IconUnderline';
import { FONT_FAILED_TOAST_KEY } from 'editor/src/component/TextStyleControl/constants';
import {
  formatNumber,
  getTextStylePreviewCss,
  isLightColor,
} from 'editor/src/component/TextStyleControl/textStylePreview';

import TextStyleFontPicker from './TextStyleFontPicker';

import styles from './index.module.scss';

const ALIGNMENTS = [
  { value: TextAlign.left, Icon: IconTextAlignLeft, labelKey: 'editor-align-left' },
  { value: TextAlign.center, Icon: IconTextAlignCenter, labelKey: 'editor-align-center' },
  { value: TextAlign.right, Icon: IconTextAlignRight, labelKey: 'editor-align-right' },
];

interface Props {
  style: TextStyle;
  /** A style just created: its name is focused and selected, ready to type over. */
  focusName?: boolean;
  onClose: () => void;
}

/**
 * GAPI-61895: "Edit style" as a view of its own that replaces the Text tab's content (drill-in,
 * like the side panels of Canva, Figma or Google Slides): back to "Text styles", a live preview,
 * name, font (the toolbar's font list at the panel's height), size, line height, letter spacing,
 * paragraph and section spacing, colour, alignment, underline and strikethrough, and Cancel / Done
 * at the bottom. The optional ones govern the texts only while set: an empty field, or the active
 * alignment clicked again, leaves each text's own value alone.
 *
 * Every change applies live to all texts following the style (except the ones locked for the
 * user, see isTextStyleProtected); the whole session is one undo step: Done (or Back) keeps it,
 * Cancel puts everything back. Escape only closes the font list or the colour picker.
 */
function TextStyleEditorView({ style, focusName, onClose }: Props) {
  const { t } = useTranslation();
  const tr = useTr();
  const dispatch = useDispatch();
  const colorPickerConfig = useSelector((state) => getHostSetting(state, 'colorPickerConfig'));
  const [name, setName] = useState(style.name);
  const [size, setSize] = useState(formatNumber(style.fontSize));
  const [lineHeight, setLineHeight] = useState(formatNumber(style.lineHeight));
  const [letterSpacing, setLetterSpacing] = useState(formatNumber(style.letterSpacing));
  const [paragraphSpacing, setParagraphSpacing] = useState(formatNumber(style.paragraphSpacing));
  const [sectionSpacing, setSectionSpacing] = useState(formatNumber(style.sectionSpacing));
  const [fontOpen, setFontOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const colorRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const session = useRef<TextStyleEditSession>();

  useEffect(() => {
    if (focusName) {
      nameRef.current?.focus();
      nameRef.current?.select();
    }
  }, []);

  // Undo/redo and font-load failures change the style under the view.
  useEffect(() => setName(style.name), [style.name]);
  useEffect(() => setSize(formatNumber(style.fontSize)), [style.fontSize]);
  useEffect(() => setLineHeight(formatNumber(style.lineHeight)), [style.lineHeight]);
  useEffect(() => setLetterSpacing(formatNumber(style.letterSpacing)), [style.letterSpacing]);
  useEffect(() => setParagraphSpacing(formatNumber(style.paragraphSpacing)), [style.paragraphSpacing]);
  useEffect(() => setSectionSpacing(formatNumber(style.sectionSpacing)), [style.sectionSpacing]);

  // The colour picker closes like a dropdown: on a click outside it, or on Escape wherever the
  // focus is (a palette swatch does not take it).
  useOnClickOutside(colorRef, () => setColorOpen(false), !colorOpen);
  useEffect(() => {
    if (!colorOpen) {
      return undefined;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setColorOpen(false);
      }
    };
    // Capture: the view stops key events from bubbling out to the canvas shortcuts.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [colorOpen]);
  useEffect(() => {
    if (colorOpen) {
      colorRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [colorOpen]);

  // Edits still waiting for their font: Done keeps them, so it commits once they have landed.
  const pending = useRef(new Set<Promise<unknown>>());
  const cancelled = useRef(false);
  const keepAfterPending = (open: TextStyleEditSession) => {
    void Promise.all([...pending.current]).then(() => dispatch(commitTextStyleEditOperation(open)));
  };

  useEffect(() => {
    session.current = dispatch(beginTextStyleEditOperation(style.id));
    return () => {
      // Closed some other way than Done/Back/Cancel (tab switch, style deleted): keep the edits.
      if (session.current) {
        keepAfterPending(session.current);
        session.current = undefined;
      }
    };
  }, []);

  const update = async (change: Partial<Omit<TextStyle, 'id'>>) => {
    // An edit whose font is still loading when the view is cancelled is dropped.
    const promise = dispatch(
      updateTextStyleOperation(style.id, change, { saveUndo: false, isCurrent: () => !cancelled.current }),
    );
    pending.current.add(promise);
    const result = await promise;
    pending.current.delete(promise);
    if (result === 'failed') {
      toastController.warning(t(FONT_FAILED_TOAST_KEY), '');
    }
  };

  // Part of the session too: Cancel puts the old name back.
  const commitName = () => {
    const next = name.trim();
    if (next && next !== style.name) {
      void update({ name: next });
    } else {
      setName(style.name);
    }
  };

  const changeSize = (value: string) => {
    setSize(value);
    const next = parseFloat(value);
    if (Number.isFinite(next) && next >= 1 && formatNumber(next) !== formatNumber(style.fontSize)) {
      void update({ fontSize: next });
    }
  };

  const changeLineHeight = (value: string) => {
    setLineHeight(value);
    if (value.trim() === '') {
      if (style.lineHeight !== undefined) {
        void update({ lineHeight: undefined });
      }
      return;
    }
    const next = parseFloat(value);
    if (Number.isFinite(next) && next > 0 && formatNumber(next) !== formatNumber(style.lineHeight)) {
      void update({ lineHeight: next });
    }
  };

  const changeLetterSpacing = (value: string) => {
    setLetterSpacing(value);
    if (value.trim() === '') {
      if (style.letterSpacing !== undefined) {
        void update({ letterSpacing: undefined });
      }
      return;
    }
    const next = parseFloat(value);
    if (Number.isFinite(next) && formatNumber(next) !== formatNumber(style.letterSpacing)) {
      void update({ letterSpacing: next });
    }
  };

  const changeSpacing = (
    key: 'paragraphSpacing' | 'sectionSpacing',
    value: string,
    setValue: (value: string) => void,
  ) => {
    setValue(value);
    if (value.trim() === '') {
      if (style[key] !== undefined) {
        void update({ [key]: undefined });
      }
      return;
    }
    const next = parseFloat(value);
    if (Number.isFinite(next) && next >= 0 && formatNumber(next) !== formatNumber(style[key])) {
      void update({ [key]: next });
    }
  };

  const finish = (keep: boolean) => {
    const { current } = session;
    session.current = undefined;
    if (current && keep) {
      keepAfterPending(current);
    } else if (current) {
      cancelled.current = true;
      void dispatch(cancelTextStyleEditOperation(current));
    }
    onClose();
  };

  const color = style.color ? getBrowserFriendlyColor(style.color) : undefined;

  return (
    <div
      className={cn(styles.editorView, 'cy-text-style-editor')}
      onKeyDown={(e) => {
        // Keep Delete/arrows/undo shortcuts in the view away from the canvas.
        e.stopPropagation();
      }}
    >
      <div className={styles.editorHeader}>
        <button
          type="button"
          className={cn(styles.backButton, 'cy-text-style-editor-back')}
          aria-label={t('editor-back')}
          onClick={() => finish(true)}
        >
          <IconArrowLeft />
          <span>{t('editor-text-styles-title')}</span>
        </button>
        <H1 noMargin noPadding className={styles.editorTitle}>
          {t('editor-text-style-edit')}
        </H1>
      </div>

      <div className={cn(styles.editorBody, { [styles.fontListOpen]: fontOpen })}>
        <div className={cn(styles.editorPreview, 'cy-text-style-editor-preview')}>
          <span
            className={cn(styles.preview, { [styles.previewOnDark]: isLightColor(style.color) })}
            style={getTextStylePreviewCss(style)}
          >
            {style.name}
          </span>
        </div>

        {!fontOpen && (
          <label className={styles.field}>
            <span>{t('editor-text-style-name')}</span>
            <input
              ref={nameRef}
              className={cn(styles.input, 'cy-text-style-name-input')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
            />
          </label>
        )}

        <div className={cn(styles.field, { [styles.fontField]: fontOpen })}>
          <span>{t('editor-text-style-font')}</span>
          <TextStyleFontPicker
            fontFamily={style.fontFamily}
            open={fontOpen}
            onOpenChange={setFontOpen}
            onChange={(fontFamily) => void update({ fontFamily })}
          />
        </div>

        {!fontOpen && (
          <>
            <div className={styles.fieldRow}>
              <label className={styles.field}>
                <span>{t('editor-text-style-size')}</span>
                <input
                  className={cn(styles.input, 'cy-text-style-size')}
                  type="number"
                  min={1}
                  value={size}
                  onChange={(e) => changeSize(e.target.value)}
                  onBlur={() => setSize(formatNumber(style.fontSize))}
                />
              </label>
              <label className={styles.field}>
                <span>{t('editor-text-style-line-height')}</span>
                <input
                  className={cn(styles.input, 'cy-text-style-line-height')}
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={lineHeight}
                  placeholder="—"
                  onChange={(e) => changeLineHeight(e.target.value)}
                  onBlur={() => setLineHeight(formatNumber(style.lineHeight))}
                />
              </label>
              <label className={styles.field}>
                <span>{t('editor-text-style-letter-spacing')}</span>
                <input
                  className={cn(styles.input, 'cy-text-style-letter-spacing')}
                  type="number"
                  step={10}
                  value={letterSpacing}
                  placeholder="—"
                  onChange={(e) => changeLetterSpacing(e.target.value)}
                  onBlur={() => setLetterSpacing(formatNumber(style.letterSpacing))}
                />
              </label>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.field}>
                <span>{tr('editor-text-style-paragraph-spacing', 'Paragraph spacing')}</span>
                <input
                  className={cn(styles.input, 'cy-text-style-paragraph-spacing')}
                  type="number"
                  min={0}
                  step={1}
                  value={paragraphSpacing}
                  placeholder="—"
                  onChange={(e) => changeSpacing('paragraphSpacing', e.target.value, setParagraphSpacing)}
                  onBlur={() => setParagraphSpacing(formatNumber(style.paragraphSpacing))}
                />
              </label>
              <label className={styles.field}>
                <span>{tr('editor-text-style-section-spacing', 'Section spacing')}</span>
                <input
                  className={cn(styles.input, 'cy-text-style-section-spacing')}
                  type="number"
                  min={0}
                  step={1}
                  value={sectionSpacing}
                  placeholder="—"
                  onChange={(e) => changeSpacing('sectionSpacing', e.target.value, setSectionSpacing)}
                  onBlur={() => setSectionSpacing(formatNumber(style.sectionSpacing))}
                />
              </label>
            </div>
            <div className={styles.fieldHint}>
              {tr(
                'editor-text-style-spacing-hint',
                'In pt: the height of one empty line between paragraphs, and of two or more empty lines between sections.',
              )}
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <span>{t('editor-text-alignment')}</span>
                <div className={styles.segmented} role="group" aria-label={t('editor-text-alignment')}>
                  {ALIGNMENTS.map(({ value, Icon, labelKey }) => (
                    <button
                      key={value}
                      type="button"
                      className={cn(styles.segment, `cy-text-style-align-${value}`, {
                        [styles.segmentOn]: style.textAlign === value,
                      })}
                      aria-label={t(labelKey)}
                      aria-pressed={style.textAlign === value}
                      onClick={() => void update({ textAlign: style.textAlign === value ? undefined : value })}
                    >
                      <Icon />
                    </button>
                  ))}
                </div>
              </div>
              <div className={cn(styles.field, styles.decorationField)}>
                <span>{t('editor-text-style-decoration')}</span>
                <div className={styles.segmented}>
                  <button
                    type="button"
                    className={cn(styles.segment, 'cy-text-style-underline', { [styles.segmentOn]: style.underline })}
                    aria-label={t('editor-underline')}
                    aria-pressed={!!style.underline}
                    onClick={() => void update({ underline: !style.underline })}
                  >
                    <IconUnderline />
                  </button>
                  <button
                    type="button"
                    className={cn(styles.segment, 'cy-text-style-strikethrough', {
                      [styles.segmentOn]: style.linethrough,
                    })}
                    aria-label={t('editor-strikethrough')}
                    aria-pressed={!!style.linethrough}
                    onClick={() => void update({ linethrough: !style.linethrough })}
                  >
                    <IconStrikethrough />
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.field} ref={colorRef}>
              <span>{t('editor-text-style-color')}</span>
              <button
                type="button"
                className={cn(styles.input, styles.colorTrigger, 'cy-text-style-color')}
                aria-label={t('editor-text-style-color')}
                aria-expanded={colorOpen}
                onClick={() => setColorOpen(!colorOpen)}
              >
                <span className={styles.swatch} style={{ background: color }} />
                <span className={styles.colorValue}>{style.color ?? '—'}</span>
              </button>
              {colorOpen && (
                <div className={cn(styles.colorPicker, 'cy-text-style-color-picker')}>
                  <ColorPicker
                    color={style.color}
                    colors={colorPickerConfig?.allowedColors}
                    allowOpacity={colorPickerConfig?.enableOpacity}
                    paletteColorsInRowCount={7}
                    onColorChange={(next) => next && void update({ color: next })}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles.editorFooter}>
        <Button
          secondary
          className={cn(styles.footerButton, 'cy-text-style-edit-cancel')}
          onClick={() => finish(false)}
        >
          {t('editor-cancel')}
        </Button>
        <Button primary className={cn(styles.footerButton, 'cy-text-style-edit-done')} onClick={() => finish(true)}>
          {t('editor-done')}
        </Button>
      </div>
    </div>
  );
}

export default React.memo(TextStyleEditorView);
